import './parallax-el.scss';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import {
    parseResponsiveAttribute,
    retrieveScrollTriggerAttributes,
} from '../web-components-utils.js';

gsap.registerPlugin(ScrollTrigger);

const template = document.createElement('template');
template.innerHTML = /* html */ `
    <slot></slot>
`;

class ParallaxEl extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        // fixes a bug where `this` is undefined after a dynamic page change
        setTimeout(() => {
            if (this.getAttribute('auto-init') == 'false') return;
            this.init();
        }, 0);
    }

    init() {
        let parallax_translate_y = parseResponsiveAttribute.call(
            this,
            this.getAttribute('translate-y')
        );

        let parallax_translate_x = parseResponsiveAttribute.call(
            this,
            this.getAttribute('translate-x')
        );
        let parallax_scale = parseResponsiveAttribute.call(this, this.getAttribute('scale')) ?? 1;
        if (parallax_scale && parallax_scale <= 0) {
            console.error('[parallax-el] Parallax scale must be greater than 0');
            parallax_scale = 1;
        }
        const parallax =
            parallax_translate_y || parallax_translate_x || parallax_scale ? true : false;

        let hover_scale = parseResponsiveAttribute.call(this, this.getAttribute('hover-scale'));
        if (hover_scale && hover_scale <= 0) {
            console.error('[parallax-el] Hover scale must be greater than 0');
        }
        const hover = hover_scale ? true : false;

        const el =
            this.querySelector('picture') ||
            this.querySelector('img') ||
            this.querySelector('video') ||
            this.querySelector('figure');

        const st_attr = retrieveScrollTriggerAttributes.call(this);

        if (parallax) {
            let tween_parameters = {
                ease: st_attr.ease || 'none',
                duration: st_attr.duration || 1,
            };
            if (!st_attr.disable) {
                tween_parameters.scrollTrigger = {
                    trigger: st_attr.trigger || this,
                    endTrigger: st_attr.endTrigger || this,
                    start: st_attr.start || 'top bottom',
                    end: st_attr.end || 'bottom top',
                    scrub: st_attr.scrub || 1,
                };
                if (st_attr.scroller) {
                    tween_parameters.scrollTrigger.scroller = st_attr.scroller;
                }
            }

            if (parallax_translate_y) {
                gsap.set(el, { height: `calc(100% + ${Math.abs(parallax_translate_y)}px)` });
                if (parallax_translate_y < 0) {
                    gsap.set(el, { top: parallax_translate_y });
                }
                tween_parameters.y = -parallax_translate_y;
            }

            if (parallax_translate_x) {
                gsap.set(el, {
                    width: `calc(100% + ${Math.abs(parallax_translate_x)}px)`,
                });
                if (parallax_translate_x > 0) {
                    gsap.set(el, { left: -parallax_translate_x });
                }
                tween_parameters.x = parallax_translate_x;
            }

            if (parallax_scale) {
                if (parallax_scale >= 1) {
                    tween_parameters.scale = parallax_scale;
                } else {
                    gsap.set(el, { scale: 1 / parallax_scale });
                    tween_parameters.scale = 1;
                }
            }

            this.tween = gsap.to(el, tween_parameters);
            if (st_attr.disable) {
                this.tween.pause();
            }
        }

        if (hover) {
            let hover_container = document.createElement('div');
            hover_container.style.height = '100%';
            hover_container.style.width = '100%';
            this.appendChild(hover_container);
            hover_container.appendChild(el);

            if (hover_scale) {
                let initial_scale = 1;
                let destination_scale = hover_scale;
                if (hover_scale < 1) {
                    initial_scale = 1 / hover_scale;
                    destination_scale = 1;
                    gsap.set(hover_container, { scale: initial_scale });
                }
                el.addEventListener('pointerenter', () => {
                    gsap.to(hover_container, {
                        scale: destination_scale,
                        duration: 0.5,
                        ease: 'power2.out',
                    });
                });
                el.addEventListener('pointerleave', () => {
                    gsap.to(hover_container, {
                        scale: initial_scale,
                        duration: 0.5,
                        ease: 'power2.out',
                    });
                });
            }
        }
    }

    disconnectedCallback() {
        this.tween?.kill();
        this.tween = null;
    }
}

window.customElements.define('parallax-el', ParallaxEl);
